import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useIsSetToHidden, useProductAccess } from 'src/shared/hooks/useProductLicensing';
import useHasRiskResults from 'src/app/portfolio/views/global-nature-risk/hooks/useHasRiskResults';
import { ProductKey } from 'src/shared/types/engine/products';
import { ProductAccess } from 'src/shared/types/productLicensing';
import NestedSidebarMenuItem from './NestedSidebarMenuItem';
import { getSiteNatureRiskSiteId } from './siteNatureRiskHelper';

const SiteNatureRiskMenu = () => {
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const hasRiskResults = useHasRiskResults();
    const access = useProductAccess(ProductKey.GLOBAL_NATURE_RISK, hasRiskResults);
    // Only an internal user reaches the menu when it is set to Hidden; the mark tells them customers do not.
    const isHiddenFromCustomers = useIsSetToHidden(ProductKey.GLOBAL_NATURE_RISK);

    const siteId = getSiteNatureRiskSiteId(pathname);

    // Every site page is "not found" without a licence, so no menu to them either.
    // Nothing while loading as well: a placeholder would show a hidden
    // product's name to the customer it is hidden from.
    if (!siteId || access !== ProductAccess.LICENSED) {
        return null;
    }

    const isSiteNatureRiskActive = pathname.startsWith('/site-nature-risk/');
    const subItems = [
        {
            title: 'Overview',
            testId: 'site-nature-risk-overview-menu-item',
            to: navigate.paths.siteNatureRiskOverview(siteId),
            isHiddenFromCustomers,
            isActive: pathname.includes('/overview'),
        },
        {
            title: 'Characteristics',
            testId: 'site-nature-risk-characteristics-menu-item',
            to: navigate.paths.siteNatureRiskCharacteristics(siteId),
            isHiddenFromCustomers,
            isActive: pathname.includes('/characteristics'),
        },
        {
            title: 'State of Nature',
            testId: 'site-nature-risk-state-of-nature-menu-item',
            to: navigate.paths.siteNatureRiskStateOfNature(siteId),
            isHiddenFromCustomers,
            isActive: pathname.includes('/state-of-nature'),
        },
        {
            title: 'Impacts & Dependencies',
            testId: 'site-nature-risk-impacts-and-dependencies-menu-item',
            to: navigate.paths.siteNatureRiskImpactsAndDependencies(siteId),
            isHiddenFromCustomers,
            isActive: pathname.includes('/impacts-and-dependencies'),
        },
    ];

    return (
        <NestedSidebarMenuItem
            label='Nature Risk'
            testId='site-nature-risk-menu-button'
            icon={<ShieldAlert />}
            isActive={isSiteNatureRiskActive}
            defaultOpen={isSiteNatureRiskActive}
            subItems={subItems}
        />
    );
};

export default SiteNatureRiskMenu;
